import { Link } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import {
  Calendar,
  Clock,
  Edit3,
  Mail,
  MapPin,
  Phone,
  Shield,
  User as UserIcon,
} from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useCurrentUserProfile } from '@/hooks/useCurrentUserProfile'
import { paths } from '@/routes/path'

const formatDate = (value: string | null | undefined) => {
  if (!value) return '—'
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(value))
  } catch {
    return '—'
  }
}

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '—'
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value))
  } catch {
    return '—'
  }
}

export function UserProfilePage() {
  const { profile, userId, isLoading, isFetching, isError, error } = useCurrentUserProfile()

  if (!userId) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
        <UserIcon className="mb-4 h-12 w-12 text-secondary-400" />
        <p className="text-lg font-semibold text-secondary-900">No user session</p>
        <p className="mt-1 max-w-md text-sm text-secondary-600">
          You need to be signed in to view your profile information.
        </p>
      </div>
    )
  }

  if (isLoading || isFetching) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (isError || !profile) {
    const message = error instanceof Error ? error.message : 'We were unable to load your profile details.'
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
        <UserIcon className="mb-4 h-12 w-12 text-secondary-400" />
        <p className="text-lg font-semibold text-secondary-900">Profile not available</p>
        <p className="mt-1 max-w-md text-sm text-secondary-600">{message}</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to={paths.app.dashboard.absolute}>Return to dashboard</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-secondary-900">My profile</h1>
          <p className="text-secondary-600">
            Review your personal information and keep your contact details up to date.
          </p>
        </div>
        <Button asChild className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700">
          <Link to={paths.app.profileEdit.absolute}>
            <Edit3 className="h-4 w-4" />
            Edit profile
          </Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1 border border-secondary-100 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <Avatar className="h-24 w-24">
                <AvatarImage src={profile.avatar ?? undefined} alt={profile.fullName || 'User avatar'} />
                <AvatarFallback className="bg-primary-100 text-primary-700">
                  {profile.initials || profile.firstName?.[0] || '?'}
                </AvatarFallback>
              </Avatar>
              <h2 className="mt-4 text-xl font-semibold text-secondary-900">{profile.fullName || 'Unnamed user'}</h2>
              <p className="text-sm text-secondary-600">{profile.email || 'No email provided'}</p>
              <Badge variant="secondary" className="mt-3 capitalize">
                {profile.role || '—'}
              </Badge>

              {profile.bio && (
                <p className="mt-5 text-sm leading-6 text-secondary-600">{profile.bio}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 border border-secondary-100 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold text-secondary-900">Contact information</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <ProfileField icon={Phone} label="Phone" value={profile.phone || '—'} />
            <ProfileField icon={Mail} label="Email" value={profile.email || '—'} />
            <ProfileField icon={MapPin} label="Department" value={profile.departmentName || profile.programmeTitle || '—'} />
            <ProfileField icon={Shield} label="Role" value={profile.role || '—'} />
            <ProfileField icon={Calendar} label="Member since" value={formatDate(profile.createdAt)} />
            <ProfileField icon={Clock} label="Last login" value={formatDateTime(profile.lastLoginAt)} />
          </CardContent>
        </Card>
      </div>

      <Card className="border border-secondary-100 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold text-secondary-900">Academic details</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <ProfileField icon={UserIcon} label="Student ID" value={profile.studentId || '—'} />
          <ProfileField icon={Calendar} label="Enrollment date" value={formatDate(profile.enrollmentDate)} />
          <ProfileField icon={Shield} label="Programme" value={profile.programmeTitle || profile.program || '—'} />
          <ProfileField icon={Shield} label="Level / Stage" value={profile.level ? `Level ${profile.level}` : '—'} />
        </CardContent>
      </Card>
    </div>
  )
}

type ProfileFieldProps = {
  icon: LucideIcon
  label: string
  value: string
}

function ProfileField({ icon: Icon, label, value }: ProfileFieldProps) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-secondary-100 p-4">
      <Icon className="mt-0.5 h-5 w-5 text-primary-600" />
      <div>
        <p className="text-sm font-medium text-secondary-500">{label}</p>
        <p className="text-sm font-semibold text-secondary-900">{value}</p>
      </div>
    </div>
  )
}
